import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FaSync,
  FaUser,
  FaBoxes,
  FaCrown,
  FaChartLine,
} from 'react-icons/fa';
import {
  fetchAllUsers,
  fetchAllProducts,
  fetchBestsellers,
  fetchRevenueStats,
  fetchAdminOrders,
  fetchTopProducts,
  fetchFailedInvoices,
  getCurrentUser,
} from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../LanguageSelector';

import KpiCard from './KpiCard';
import RevenueTrendCard from './RevenueTrendCard';
import RecentOrdersCard from './RecentOrdersCard';
import TopProductsCard from './TopProductsCard';
import QuickActionsCard from './QuickActionsCard';
import FailedInvoicesAlert from './FailedInvoicesAlert';
import { formatCompactMoney, formatNumber, getDateRange } from './formatters';

const RANGE_PRESETS = ['today', 'last7', 'last30'];

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};

const formatLongDate = (lang) => {
  try {
    return new Intl.DateTimeFormat(lang || 'en', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(new Date());
  } catch {
    return new Date().toLocaleDateString();
  }
};

const AdminDashboard = () => {
  const { t, language } = useLanguage();

  const [preset, setPreset] = useState('last30');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [totals, setTotals] = useState({ users: 0, products: 0, bestsellers: 0, revenue: 0 });
  const [revenuePoints, setRevenuePoints] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [failedInvoiceCount, setFailedInvoiceCount] = useState(0);

  useEffect(() => {
    try {
      const u = getCurrentUser();
      setCurrentUser(u);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const { from, to } = getDateRange(preset);

    const [
      usersRes,
      productsRes,
      bestsellersRes,
      revenueRes,
      ordersRes,
      topRes,
      failedRes,
    ] = await Promise.allSettled([
      fetchAllUsers(),
      fetchAllProducts(1),
      fetchBestsellers(),
      fetchRevenueStats(from, to, 'day'),
      fetchAdminOrders({ page: 1, limit: 6 }),
      fetchTopProducts(from, to, 5),
      fetchFailedInvoices(),
    ]);

    const nextTotals = { users: 0, products: 0, bestsellers: 0, revenue: 0 };

    if (usersRes.status === 'fulfilled') {
      nextTotals.users = usersRes.value?.data?.users?.length || 0;
    }
    if (productsRes.status === 'fulfilled') {
      const d = productsRes.value?.data || {};
      nextTotals.products = d.totalProducts || d.total || d.count || 0;
    }
    if (bestsellersRes.status === 'fulfilled') {
      nextTotals.bestsellers = bestsellersRes.value?.data?.total || 0;
    }
    if (revenueRes.status === 'fulfilled' && revenueRes.value?.data) {
      nextTotals.revenue = revenueRes.value.data.totals?.revenue || 0;
      setRevenuePoints(revenueRes.value.data.dataPoints || []);
    } else {
      setRevenuePoints([]);
    }

    setTotals(nextTotals);

    if (ordersRes.status === 'fulfilled') {
      const items = ordersRes.value?.data?.items;
      setRecentOrders(Array.isArray(items) ? items : []);
    } else {
      setRecentOrders([]);
    }

    if (topRes.status === 'fulfilled') {
      setTopProducts(topRes.value?.data?.products || []);
    } else {
      setTopProducts([]);
    }

    if (failedRes.status === 'fulfilled') {
      setFailedInvoiceCount(failedRes.value?.data?.count || 0);
    } else {
      setFailedInvoiceCount(0);
    }

    const anyFailed = [
      usersRes,
      productsRes,
      bestsellersRes,
      revenueRes,
      ordersRes,
      topRes,
    ].some((r) => r.status === 'rejected');
    if (anyFailed) setError(t('dashboard.errors.fetchData'));

    setIsLoading(false);
  }, [preset, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const welcomeName = useMemo(() => {
    const u = currentUser;
    const name = u?.name || u?.firstName || u?.username;
    return name
      ? t('dashboard.welcome', { name })
      : t('dashboard.welcomeFallback');
  }, [currentUser, t]);

  const heroGreeting = useMemo(() => {
    const tod = getTimeOfDay();
    const u = currentUser;
    const firstName =
      u?.firstName ||
      (typeof u?.name === 'string' ? u.name.split(' ')[0] : null) ||
      u?.username ||
      null;
    return firstName
      ? t(`dashboard.greeting.${tod}`, { name: firstName })
      : t(`dashboard.greetingFallback.${tod}`);
  }, [currentUser, t]);

  const heroInitial = useMemo(() => {
    const u = currentUser;
    const src = u?.firstName || u?.name || u?.username || '';
    return src ? src.charAt(0).toUpperCase() : '';
  }, [currentUser]);

  const todayLong = useMemo(() => formatLongDate(language), [language]);

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="tablist"
        aria-label={t('dashboard.dateRange.label')}
        className="inline-flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm"
      >
        {RANGE_PRESETS.map((p) => (
          <button
            key={p}
            onClick={() => setPreset(p)}
            role="tab"
            aria-selected={preset === p}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              preset === p
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t(`dashboard.dateRange.${p}`)}
          </button>
        ))}
      </div>
      <LanguageSelector />
      <button
        onClick={fetchAll}
        disabled={isLoading}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3.5 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium disabled:opacity-60"
      >
        <FaSync className={`text-xs ${isLoading ? 'animate-spin' : ''}`} />
        {t('dashboard.refreshData')}
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6 py-6">
        {/* Mobile hero (hidden on lg+) */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="lg:hidden mb-4 relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-5 shadow-lg"
        >
          <div className="absolute -top-12 -right-10 w-36 h-36 bg-white/15 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-14 -left-10 w-44 h-44 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex items-center gap-3">
            {heroInitial && (
              <div className="flex-none w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center text-white font-bold text-lg">
                {heroInitial}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-white text-lg font-bold tracking-tight leading-tight">
                {heroGreeting}
              </h1>
              <p className="text-white/80 text-xs mt-1 capitalize">{todayLong}</p>
            </div>
          </div>
        </motion.div>

        {/* Mobile controls row (hidden on lg+) */}
        <div className="lg:hidden mb-5">{controls}</div>

        {/* Desktop header (hidden on mobile) */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="hidden lg:flex mb-6 items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              {t('dashboard.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{welcomeName}</p>
          </div>
          {controls}
        </motion.div>

        <FailedInvoicesAlert count={failedInvoiceCount} />

        {error && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            icon={FaUser}
            label={t('dashboard.stats.totalUsers')}
            value={formatNumber(totals.users)}
            sub={t('dashboard.stats.activeUserAccounts')}
            accent="blue"
            isLoading={isLoading}
            delay={0}
          />
          <KpiCard
            icon={FaBoxes}
            label={t('dashboard.stats.totalProducts')}
            value={formatNumber(totals.products)}
            sub={t('dashboard.stats.productsInCatalog')}
            accent="purple"
            isLoading={isLoading}
            delay={0.05}
          />
          <KpiCard
            icon={FaCrown}
            label={t('dashboard.stats.bestsellers')}
            value={formatNumber(totals.bestsellers)}
            sub={t('dashboard.stats.featuredProducts')}
            accent="yellow"
            isLoading={isLoading}
            delay={0.1}
          />
          <KpiCard
            icon={FaChartLine}
            label={t('dashboard.stats.revenue')}
            value={formatCompactMoney(totals.revenue)}
            sub={t('dashboard.stats.revenueInRange')}
            accent="green"
            isLoading={isLoading}
            delay={0.15}
          />
        </div>

        {/* Main grid: revenue trend + recent orders */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <RevenueTrendCard
              dataPoints={revenuePoints}
              isLoading={isLoading}
              delay={0.05}
            />
          </div>
          <RecentOrdersCard
            orders={recentOrders}
            isLoading={isLoading}
            delay={0.1}
          />
        </div>

        {/* Secondary grid: top products + quick actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <TopProductsCard
              products={topProducts}
              isLoading={isLoading}
              delay={0.05}
            />
          </div>
          <QuickActionsCard delay={0.1} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
